import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Component, OnDestroy, OnInit, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ApexThemeService } from 'shared-ui';
import { PlatformAdminPortalService } from '../../core/services/platform-admin-portal.service';
import { PlatformAdminWorkspaceService } from '../../core/services/platform-admin-workspace.service';

type PlatformAdminNavItem = {
  label: string;
  icon: string;
  route: string;
  /** Not connected to platform data yet; the page says so instead of showing sample figures. */
  soon?: boolean;
};

type PlatformAdminNavGroup = {
  label: string | null;
  items: PlatformAdminNavItem[];
};

@Component({
  selector: 'app-platform-admin-shell',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive, RouterOutlet],
  templateUrl: './platform-admin-shell.html',
  styleUrl: './platform-admin-shell.scss',
})
export class PlatformAdminShell implements OnInit, OnDestroy {
  private readonly workspace = inject(PlatformAdminWorkspaceService);
  private readonly api = inject(PlatformAdminPortalService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly theme = inject(ApexThemeService);
  private notificationPollTimer: ReturnType<typeof setInterval> | null = null;

  protected readonly menuOpen = signal(false);
  protected readonly notificationOpen = signal(false);
  protected readonly pendingDeletionCount = signal(0);
  protected readonly pendingDeletionCountLoading = signal(false);

  /** Sections of the side menu, by what the admin is doing; the first has no heading. */
  protected readonly navGroups: PlatformAdminNavGroup[] = [
    {
      label: null,
      items: [
        { label: 'Overview', icon: 'dashboard', route: '/dashboard' },
        { label: 'Reports', icon: 'monitoring', route: '/reports' },
      ],
    },
    {
      label: 'Customers',
      items: [
        { label: 'Organizations', icon: 'domain', route: '/organizations' },
        { label: 'Users', icon: 'groups', route: '/users' },
        { label: 'Invitations', icon: 'outgoing_mail', route: '/invitations' },
        { label: 'Account deletions', icon: 'person_remove', route: '/account-deletions' },
      ],
    },
    {
      label: 'Billing & payments',
      items: [
        { label: 'Subscriptions', icon: 'workspace_premium', route: '/subscriptions' },
        { label: 'Payment Fees', icon: 'credit_card', route: '/payment-fees' },
        { label: 'Screening Settings', icon: 'policy', route: '/screening' },
        { label: 'Transactions', icon: 'sync_alt', route: '/transactions' },
      ],
    },
    {
      label: 'Security & system',
      items: [
        { label: 'Roles & Permissions', icon: 'admin_panel_settings', route: '/roles' },
        { label: 'Security & Access', icon: 'shield_lock', route: '/security' },
        { label: 'Audit Logs', icon: 'receipt_long', route: '/audit-logs' },
        { label: 'Integrations', icon: 'hub', route: '/integrations' },
      ],
    },
  ];

  protected readonly orgName = computed(() => this.workspace.displayName());

  protected readonly initials = computed(() => {
    return this.orgName()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('') || 'PA';
  });

  protected readonly pendingDeletionBadge = computed(() => {
    const count = this.pendingDeletionCount();
    if (count <= 0) return null;
    return count > 99 ? '99+' : String(count);
  });

  async ngOnInit(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    await this.refreshNotificationCount();
    this.notificationPollTimer = setInterval(() => {
      void this.refreshNotificationCount();
    }, 60000);
  }

  ngOnDestroy(): void {
    if (this.notificationPollTimer) {
      clearInterval(this.notificationPollTimer);
      this.notificationPollTimer = null;
    }
  }

  protected toggleTheme(): void {
    this.theme.toggleLightDark();
  }

  protected closeMenu(): void {
    this.menuOpen.set(false);
  }

  protected toggleNotifications(): void {
    this.notificationOpen.update((open) => !open);
    void this.refreshNotificationCount();
  }

  protected closeNotifications(): void {
    this.notificationOpen.set(false);
  }

  protected notificationSummary(): string {
    const count = this.pendingDeletionCount();
    if (count <= 0) {
      return 'No pending deletion requests';
    }
    return `${count} pending deletion request${count === 1 ? '' : 's'}`;
  }

  protected signOut(): void {
    void this.workspace.signOut();
  }

  private async refreshNotificationCount(): Promise<void> {
    if (this.pendingDeletionCountLoading()) {
      return;
    }

    this.pendingDeletionCountLoading.set(true);
    try {
      const page = await firstValueFrom(this.api.getAccountDeletionRequests({
        status: 'PENDING',
        page: 0,
        size: 1,
      }));
      this.pendingDeletionCount.set(page.totalElements ?? page.content?.length ?? 0);
    } catch (error) {
      console.warn('[PlatformAdminShell] notification count lookup failed:', error);
      this.pendingDeletionCount.set(0);
    } finally {
      this.pendingDeletionCountLoading.set(false);
    }
  }
}
